import { App, Button, Flex, Input, InputNumber, Modal, Space, theme, Typography, Tag, notification } from "antd";
import { MutableRefObject, useImperativeHandle, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CODES_MAX } from "../../utils";
import { FlexWrap, CreateCodesWrap } from "./style";
import useFrontDelTip from "@/hooks/useFrontDelTip";

const { Text } = Typography;

export interface CreateCodesRef {
  getValidate: () => {
    isValid: boolean,
    codes: Array<string>
  }
}

interface CreateCodesProps {
  crRef: MutableRefObject<CreateCodesRef | null>;
  value: string;
  onChange: (val: string) => void;
  originBarcodes?: string[];
  createNum?: number;
}

function CreateCodes({ value, onChange, crRef, originBarcodes, createNum }: CreateCodesProps) {
  const { t } = useTranslation();
  const { token: { colorPrimary } } = theme.useToken();
  const { onConfirm } = useFrontDelTip<void>({
    cb: () =>
      new Promise((res) => {
        onChange("");
        res();
      }),
  });

  const [start, setStart] = useState<number>(0);
  const [end, setEnd] = useState<number>(CODES_MAX.barcodeMax - 1);
  const [suffix, setSuffix] = useState<string>("");
  const [prefix, setPrefix] = useState<string>("");
  const [isShow, setIsShow] = useState<boolean>(false); // 批量生成的弹窗是否开启

  useImperativeHandle(crRef, () => ({ getValidate }), [value]);

  // 计算不在原始条码中的条码
  const invalidCodes = useMemo(() => {
    const codes = value.split(/[\n\s]+/)
      .map((item) => item.trim())
      .filter((item) => item);

    if (!originBarcodes || originBarcodes.length === 0) {
      return new Set<string>();
    }

    const originSet = new Set(originBarcodes);
    const invalid = new Set<string>();

    codes.forEach(code => {
      if (!originSet.has(code)) {
        invalid.add(code);
      }
    });

    return invalid;
  }, [value, originBarcodes]);

  const getValidate = () => {
    const codes = Array.from(new Set(value.split("\n")))
      .map((item) => item.trim())
      .filter((item) => item);
    onChange(codes.join("\n"));
    if (codes.length === 0) {
      // notification.error({ placement: 'top',
      //   message: "校验失败",
      //   description: `数据不合法, 扫描单数据为空`,
      // });
      return { isValid: true, codes: [] };
    } else if (codes.length > CODES_MAX.barcodeMax) {
      notification.error({ placement: 'top',
        message: t("warehouse.validationFailed"),
        description: t("warehouse.dataInvalid", { max: CODES_MAX.barcodeMax }),
      });
      return { isValid: false, codes };
    } else {
      return { isValid: true, codes };
    }
  };

  const handleGenerate = () => {
    const split = end - start;

    if (start > end) {
      return notification.error({ placement: 'top',
        message: t("warehouse.generateFailed"),
        description: t("scan.endLessThanStart"),
      });
    }
    // if (split > CODES_MAX.barcodeMax) {
    //   return notification.error({ placement: 'top',
    //     message: t("warehouse.generateFailed"),
    //     description: t("warehouse.dataInvalid", { max: CODES_MAX.barcodeMax }),
    //   });
    // }

    const numLength = end.toString().length;
    let result = value ? value + "\n" : value;
    const existingCount = value ? value.split("\n").filter(item => item.trim()).length : 0;
    let generatedCount = 0;

    for (let i = start; i <= end; i++) {
      // 如果有 createNum 限制，检查是否已达到上限
      if (createNum && existingCount + generatedCount >= createNum) {
        break;
      }
      const item = i.toString().padStart(numLength, "0");
      result += `${prefix}${item}${suffix}\n`;
      generatedCount++;
    }

    // 如果因为 createNum 限制而截断，给出提示
    if (createNum && existingCount + generatedCount >= createNum) {
      notification.info({ placement: 'top',
        message: "提示",
        description: t("warehouse.maxBarcodeCount", { count: createNum })
      });
    }

    onChange(result);
    setIsShow(false)
  };

  const handleInit = () => {
    setStart(0);
    setEnd(CODES_MAX.barcodeMax - 1);
    setSuffix('');
    setPrefix('')
  }

  return (
    <CreateCodesWrap vertical gap={10} color={colorPrimary}>
      <div className="code-input--wrap">
        <Input.TextArea
          variant="borderless"
          rows={10}
          autoSize={false}
          style={{ resize: "none" }}
          value={value}
          placeholder="请输入或批量生成条形码"
          onChange={(e) => onChange(e.target.value)}
        />
        <Flex align="center" justify="space-between">
          <Button
            className="code-input--clear"
            type="link"
            onClick={() => {
              if (value) {
                onConfirm("");
              }
            }}
          >
            清除数据
          </Button>
          <Button
            className="code-input--get"
            type="primary"
            onClick={() => setIsShow(true)}
          >
            {t("warehouse.batchGenerateBarcodes")}
          </Button>
        </Flex>
      </div>
      {originBarcodes && originBarcodes.length > 0 && invalidCodes.size > 0 && (
        <div style={{ marginTop: 10, maxHeight: 120, overflow: 'auto' }}>
          <Text type="danger" strong>{t("warehouse.barcodesNotInOriginal", { barcodes: "" })}</Text>
          <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {Array.from(invalidCodes).map((code) => (
              <Tag color="error" key={code}>{code}</Tag>
            ))}
          </div>
        </div>
      )}
      <Space direction="vertical">
        <Text>备注：</Text>
        <Text type="secondary">1. 条码应该是唯一的 </Text>
        <Text type="secondary">2. 输入的条码将以「换行」分割</Text>
        {/* <Text type="secondary">3. 条码数应该大于0且小于等于500</Text> */}
        <Text type="secondary">
          3. 保存扫描单时会自动去除重复数据并校验数据合法性
        </Text>
        {originBarcodes && originBarcodes.length > 0 && (
          <Text type="secondary">
            4. <Text type="danger">红色标记</Text>的条码不在原始条码列表中
          </Text>
        )}
      </Space>
      <Modal title={t("warehouse.batchGenerateBarcodes")} open={isShow} onCancel={() => setIsShow(false)} onOk={handleGenerate} afterClose={handleInit}>
        <FlexWrap vertical justify="space-between" gap={20}>
          <Input
            addonBefore={t("scan.barcodePrefix")}
            placeholder={t("warehouse.optional")}
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
          />
          <Input
            addonBefore={t("scan.barcodeSuffix")}
            placeholder={t("warehouse.optional")}
            value={suffix}
            onChange={(e) => setSuffix(e.target.value)}
          />
          <InputNumber changeOnWheel
            addonBefore={t("scan.barcodeStartNum")}
            min={0}
            value={start}
            maxLength={4}
            onChange={(value) => value && setStart(value)}
            style={{ width: "100%" }}
          />
          <InputNumber changeOnWheel
            addonBefore={t("scan.barcodeEndNum")}
            min={1}
            value={end}
            maxLength={4}
            onChange={(value) => value && setEnd(value)}
            style={{ width: "100%" }}
          />
        </FlexWrap>
      </Modal>
    </CreateCodesWrap>
  );
}

export default CreateCodes;
